import { captureEvent } from "@/lib/analytics";

export type OnboardingUseCase = "work" | "school" | "life";

// Seeded category ids (unideck-api template-category-seed-ids.ts). After the
// use-case question, onboarding opens the real Explore Templates gallery
// narrowed to the matching category.
export const USE_CASE_CATEGORY_ID: Record<OnboardingUseCase, string> = {
  work: "3e9bff14-2f97-4f0b-9ef5-ecf61ef31f37",
  school: "215d77aa-aaec-4494-b4df-5538db7acc4b",
  life: "6b08cde7-43a1-4749-904d-ac7948d2dc40",
};

export const USE_CASE_OPTIONS: {
  value: OnboardingUseCase;
  label: string;
  description: string;
}[] = [
  { value: "work", label: "Work", description: "Projects, meetings, and the tools you work in" },
  { value: "school", label: "School", description: "Classes, assignments, and study focus" },
  { value: "life", label: "Life", description: "News, downtime, and staying organized" },
];

// --- Activation instrumentation (lightweight, localStorage-backed) ---
// These let us measure the funnel from the template-first onboarding through to
// real engagement, without a server-side events pipeline.

const SIGNUP_AT_KEY = "unideck.onboarding.signupAt";
const PENDING_FIRST_WIDGET_KEY = "unideck.onboarding.pendingFirstWidget";
const DAY2_FIRED_KEY = "unideck.onboarding.day2Fired";

const safeGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore (private mode / storage disabled)
  }
};

const safeRemove = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

// Called when a new user finishes onboarding (template chosen or start blank).
// Arms the first-widget-interaction and day-2-return trackers.
export const markOnboardingCompleted = () => {
  if (!safeGet(SIGNUP_AT_KEY)) {
    safeSet(SIGNUP_AT_KEY, Date.now().toString());
  }
  safeSet(PENDING_FIRST_WIDGET_KEY, "1");
  safeRemove(DAY2_FIRED_KEY);
};

// Fires onboarding_first_widget_interaction exactly once, on the user's first
// widget mutation after onboarding.
export const trackFirstWidgetInteraction = (
  interaction: "add" | "move" | "configure" | "remove",
) => {
  if (safeGet(PENDING_FIRST_WIDGET_KEY) !== "1") {
    return;
  }
  safeRemove(PENDING_FIRST_WIDGET_KEY);
  captureEvent("onboarding_first_widget_interaction", { interaction });
};

// Fires onboarding_day_2_return once, when an onboarded user returns roughly a
// day (or more) after signing up.
export const maybeTrackDay2Return = () => {
  const signupAtRaw = safeGet(SIGNUP_AT_KEY);
  if (!signupAtRaw || safeGet(DAY2_FIRED_KEY) === "1") {
    return;
  }

  const signupAt = Number(signupAtRaw);
  if (!Number.isFinite(signupAt)) {
    return;
  }

  const elapsedMs = Date.now() - signupAt;
  const twentyHoursMs = 20 * 60 * 60 * 1000;
  if (elapsedMs < twentyHoursMs) {
    return;
  }

  safeSet(DAY2_FIRED_KEY, "1");
  captureEvent("onboarding_day_2_return", {
    daysSinceSignup: Math.floor(elapsedMs / (24 * 60 * 60 * 1000)),
  });
};
